import { useEffect, useMemo, useState } from 'react'
import { BarChart3, Printer, Calendar, Package, TrendingUp, Building2 } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'

// Client-side reports. Scoped to the sites the client user has access to
// (RLS on orders handles the actual filter — we just read what we can).
// Date range → summary tiles → per-product breakdown → per-site breakdown
// (only shown if the client has more than one site). Printable via the
// browser's Print dialog which produces a clean PDF via "Save as PDF".
export default function ClientReports({ activeCompanyId = null }) {
  const [orders, setOrders] = useState([])
  const [lines, setLines] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(false)
  // Default range: last 90 days
  const today = new Date().toISOString().slice(0, 10)
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const [from, setFrom] = useState(ninetyDaysAgo)
  const [to, setTo] = useState(today)
  const [siteFilter, setSiteFilter] = useState('all') // 'all' | company_id

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    // Get orders in the date range (RLS restricts to this user's companies).
    // Include placed_at + status filter to skip drafts.
    let q = supabase.from('orders')
      .select('id, reference, status, placed_at, customer_po, company_id, companies(id, name)')
      .neq('status', 'draft')
      .gte('placed_at', from + 'T00:00:00')
      .lte('placed_at', to   + 'T23:59:59')
      .order('placed_at', { ascending: false })
    if (siteFilter !== 'all') q = q.eq('company_id', siteFilter)
    // If a specific active company is passed via preview mode, always scope to it
    if (activeCompanyId) q = q.eq('company_id', activeCompanyId)
    const { data: os } = await q
    setOrders(os || [])

    // Get lines for those orders
    const orderIds = (os || []).map(o => o.id)
    if (orderIds.length > 0) {
      const { data: ls } = await supabase.from('order_lines')
        .select('order_id, name_at_order, sku_at_order, qty, price_at_order')
        .in('order_id', orderIds)
      setLines(ls || [])
    } else {
      setLines([])
    }

    // Companies for the site dropdown (only ones the user can see).
    const { data: cs } = await supabase.from('companies').select('id, name').is('archived_at', null).order('name')
    setCompanies(cs || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [from, to, siteFilter, activeCompanyId])

  // ---- Aggregations
  const summary = useMemo(() => {
    let totalSpend = 0
    let totalUnits = 0
    for (const l of lines) {
      const price = Number(l.price_at_order || 0)
      const qty = Number(l.qty || 0)
      totalSpend += price * qty
      totalUnits += qty
    }
    return {
      orderCount: orders.length,
      totalSpend,
      totalUnits,
      distinctProducts: new Set(lines.map(l => l.sku_at_order || l.name_at_order)).size,
    }
  }, [orders, lines])

  const perProduct = useMemo(() => {
    const map = new Map()
    for (const l of lines) {
      const key = l.sku_at_order || l.name_at_order
      if (!map.has(key)) map.set(key, {
        name: l.name_at_order, sku: l.sku_at_order,
        qty: 0, spend: 0, orderCount: 0, orderIds: new Set(),
      })
      const row = map.get(key)
      row.qty += Number(l.qty || 0)
      row.spend += Number(l.qty || 0) * Number(l.price_at_order || 0)
      row.orderIds.add(l.order_id)
    }
    // finalise
    return [...map.values()]
      .map(r => ({ ...r, orderCount: r.orderIds.size }))
      .sort((a, b) => b.spend - a.spend)
  }, [lines])

  const perSite = useMemo(() => {
    const map = new Map()
    for (const o of orders) {
      const key = o.company_id || 'unknown'
      const name = o.companies?.name || '(unknown site)'
      if (!map.has(key)) map.set(key, { id: key, name, orderCount: 0, spend: 0 })
      map.get(key).orderCount++
    }
    for (const l of lines) {
      const o = orders.find(o => o.id === l.order_id)
      if (!o) continue
      const row = map.get(o.company_id || 'unknown')
      if (row) row.spend += Number(l.qty || 0) * Number(l.price_at_order || 0)
    }
    return [...map.values()].sort((a, b) => b.spend - a.spend)
  }, [orders, lines])

  const showSiteBreakdown = perSite.length > 1 && !activeCompanyId

  const fmtMoney = (n) => '£' + (Number(n || 0)).toFixed(2)
  const fmtDate = (s) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <div className="space-y-4 max-w-5xl print:max-w-none">
      <DemoNotice />

      {/* Controls — hidden when printing */}
      <div className="card print:hidden">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-brand" />
          <h3 className="font-semibold text-gray-800">Ordering report</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Pick a date range to see how much you've spent, how many orders, and which products.
          Print or save as PDF to share with your team.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <label>
            <span className="text-xs text-gray-500 block mb-1">From</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">To</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="input-field" />
          </label>
          {!activeCompanyId && companies.length > 1 && (
            <label className="sm:col-span-1">
              <span className="text-xs text-gray-500 block mb-1">Site</span>
              <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)} className="input-field">
                <option value="all">All sites ({companies.length})</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <button onClick={() => window.print()} className="btn-primary sm:justify-self-end">
            <Printer className="w-4 h-4" /> Download / print PDF
          </button>
        </div>
      </div>

      {/* Print-only header — appears only in print output */}
      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Ordering Report</h1>
        <p className="text-sm text-gray-600 mt-1">
          {fmtDate(from)} — {fmtDate(to)}
          {siteFilter !== 'all' && companies.find(c => c.id === siteFilter) && (
            <> · {companies.find(c => c.id === siteFilter).name}</>
          )}
        </p>
      </div>

      {loading && <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>}

      {/* Summary tiles */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile icon={TrendingUp} label="Total spend" value={fmtMoney(summary.totalSpend)} />
          <StatTile icon={Package}    label="Orders"      value={summary.orderCount} />
          <StatTile icon={Package}    label="Units"       value={summary.totalUnits} />
          <StatTile icon={Package}    label="Distinct products" value={summary.distinctProducts} />
        </div>
      )}

      {/* Per-product breakdown */}
      {!loading && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">Products ordered</h3>
          {perProduct.length === 0
            ? <p className="text-sm text-gray-500 italic">No orders in this date range.</p>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                      <th className="text-left py-2">Product</th>
                      <th className="text-right py-2 w-24">Orders</th>
                      <th className="text-right py-2 w-24">Units</th>
                      <th className="text-right py-2 w-32">Total spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perProduct.map((r, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2">
                          <div className="font-medium text-gray-800">{r.name}</div>
                          {r.sku && <div className="text-xs text-gray-500">{r.sku}</div>}
                        </td>
                        <td className="text-right tabular-nums text-gray-700">{r.orderCount}</td>
                        <td className="text-right tabular-nums text-gray-700">{r.qty}</td>
                        <td className="text-right tabular-nums font-medium text-gray-800">{fmtMoney(r.spend)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-gray-800">
                      <td className="pt-3 text-right pr-3" colSpan={3}>Total</td>
                      <td className="pt-3 text-right tabular-nums">{fmtMoney(summary.totalSpend)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* Per-site breakdown (only if user has more than one site) */}
      {!loading && showSiteBreakdown && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> By site
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Site</th>
                  <th className="text-right py-2 w-24">Orders</th>
                  <th className="text-right py-2 w-32">Spend</th>
                </tr>
              </thead>
              <tbody>
                {perSite.map(s => (
                  <tr key={s.id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-800">{s.name}</td>
                    <td className="text-right tabular-nums text-gray-700">{s.orderCount}</td>
                    <td className="text-right tabular-nums font-medium text-gray-800">{fmtMoney(s.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders list — small print, sits at the bottom */}
      {!loading && orders.length > 0 && (
        <div className="card print:break-before-page">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Orders in this period
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Reference</th>
                  <th className="text-left py-2">PO</th>
                  <th className="text-left py-2">Site</th>
                  <th className="text-left py-2">Placed</th>
                  <th className="text-left py-2 w-24">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-800">{o.reference || '—'}</td>
                    <td className="py-2 text-gray-700">{o.customer_po || '—'}</td>
                    <td className="py-2 text-gray-700">{o.companies?.name || '—'}</td>
                    <td className="py-2 text-gray-500 text-xs">{o.placed_at ? fmtDate(o.placed_at) : '—'}</td>
                    <td className="py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="stat-tile">
      <div className="section-label flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="stat-value">{value}</div>
    </div>
  )
}
